import { AlertTriangle, ExternalLink, ArrowUp, ArrowDown, X } from 'lucide-react';
import { useState } from 'react';
import { useCIStatus } from '@/hooks/useCIStatus';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const DISMISS_DURATION_MS = 15 * 60 * 1000; // 15 minutos

export function CIStatusBanner() {
  const { data: ciStatus, isLoading } = useCIStatus();
  
  const [position, setPosition] = useState<'top' | 'bottom'>(() => {
    const stored = localStorage.getItem('ci_banner_position');
    return (stored === 'bottom' ? 'bottom' : 'top') as 'top' | 'bottom';
  });

  const [isDismissed, setIsDismissed] = useState(() => {
    const dismissedUntil = localStorage.getItem('ci_banner_dismissed_until');
    if (dismissedUntil) {
      const until = parseInt(dismissedUntil, 10);
      if (Date.now() < until) return true;
    }
    return false;
  });

  const togglePosition = () => {
    const newPosition = position === 'top' ? 'bottom' : 'top';
    setPosition(newPosition);
    localStorage.setItem('ci_banner_position', newPosition);
  };

  const handleDismiss = () => {
    const dismissUntil = Date.now() + DISMISS_DURATION_MS;
    localStorage.setItem('ci_banner_dismissed_until', dismissUntil.toString());
    setIsDismissed(true);
  };
  
  if (isLoading || !ciStatus) return null;
  
  // Só mostra se houver falha
  if (ciStatus.status === 'success') return null;
  
  // Se foi dispensado, não mostrar
  if (isDismissed) return null;
  
  const timeAgo = formatDistanceToNow(new Date(ciStatus.completed_at), { 
    addSuffix: true,
    locale: ptBR 
  });
  
  return (
    <div className={`fixed left-0 right-0 z-[9999] ${
      position === 'top' 
        ? 'top-0 animate-in slide-in-from-top' 
        : 'bottom-0 animate-in slide-in-from-bottom'
    }`}>
      <Alert className="rounded-none border-x-0 border-t-0 bg-destructive/90 text-destructive-foreground border-destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle className="font-bold">
          🚨 CI Pipeline Failed - Ação Necessária
        </AlertTitle>
        <AlertDescription className="flex items-center justify-between gap-4">
          <div className="space-y-1 flex-1">
            <p className="text-sm">
              <strong>Branch:</strong> {ciStatus.branch_name} • 
              <strong> Commit:</strong> {ciStatus.commit_sha.substring(0, 7)} • 
              <strong> Falhou {timeAgo}</strong>
            </p>
            {ciStatus.failure_details?.failed_tests && (
              <p className="text-sm">
                ❌ {ciStatus.failure_details.failed_tests}/{ciStatus.failure_details.total_tests} testes falharam
              </p>
            )}
            {ciStatus.commit_message && (
              <p className="text-xs opacity-90 line-clamp-1">{ciStatus.commit_message}</p>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              className="bg-background/10 text-destructive-foreground hover:bg-background/20 shrink-0"
              onClick={handleDismiss}
              title="Dispensar por 15 minutos"
            >
              <X className="h-4 w-4" />
            </Button>
            
            <Button
              size="sm"
              variant="ghost"
              className="bg-background/10 text-destructive-foreground hover:bg-background/20 shrink-0"
              onClick={togglePosition}
              title={position === 'top' ? 'Mover para rodapé' : 'Mover para topo'}
            >
              {position === 'top' ? (
                <ArrowDown className="h-4 w-4" />
              ) : (
                <ArrowUp className="h-4 w-4" />
              )}
            </Button>
            
            {ciStatus.workflow_url && (
              <Button 
                size="sm" 
                variant="outline" 
                className="bg-background/20 text-destructive-foreground border-destructive-foreground/30 hover:bg-background/30 shrink-0"
                onClick={() => window.open(ciStatus.workflow_url!, '_blank')}
              >
                Ver Detalhes <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            )}
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
}
